import { Injectable, Injector } from '@angular/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { FolderDialogComponent } from '@alfresco/adf-content-services';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { Node, NodeEntry, SiteEntry } from '@alfresco/js-api';
import { NodeAssociationPaging } from '@alfresco/js-api/src/api/content-rest-api';

import { ContentManagementService } from '@adw/projects/aca-content/src/lib/services/content-management.service';
import {
  DeletedNodeInfo,
  DeleteStatus,
  SnackbarAction,
} from '@alfresco/aca-shared/store';
import { AppHookService, ContentApiService } from '@alfresco/aca-shared';

import { WkTreeViewService } from './wk-tree-view.service';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import { NotificationDialogComponent } from '../dialogs/notification/notification.dialog';
import {
  SnackbarErrorAction,
  SnackbarInfoAction,
  SnackbarUserAction,
  SnackbarWarningAction,
  UndoDeleteNodesAction,
} from '../store/actions/app.custom-actions';
import { ContentService } from '../components/shared/services/content.service';
import { WkSiteModel } from '../model/wk-site.model';
import { REFERENCED_ASSETS, REFERENCED_DOCUMENTS } from '../constants';
import { FolderOperationType, MediaTypeKeys } from '../enums';
import { CommonConstants } from '../shared/common.constant';
import { WkNodePermissionsDialogComponent } from '../components/permissions/wk-permission-dialog/wk-node-permissions.dialog';
import { DialogComponent, DialogSize } from '@alfresco/adf-core';
import { FolderInformationComponent } from '@adw/projects/aca-content/src/lib/dialogs/folder-details/folder-information.component';
import { NodeActionsFacade } from './node-actions.facade';

@Injectable({
  providedIn: 'root',
})
export class WkContentManagementService {
  private readonly apiService: WkAlfrescoApiService;
  private readonly contentService: ContentService;
  private readonly injector: Injector;

  constructor(
    injector: Injector,
    private readonly contentManagementService: ContentManagementService,
    private readonly contentApiService: ContentApiService,
    private readonly treeViewService: WkTreeViewService,
    private readonly dialogRef: MatDialog,
    private readonly appHookService: AppHookService
  ) {
    this.injector = injector;
    this.apiService = injector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
    this.contentService = injector.get<ContentService>(ContentService);
  }

  get nodesDeleted(): Subject<void> {
    return this.appHookService.nodesDeleted;
  }

  get libraryCreated(): Subject<SiteEntry> {
    return this.appHookService.libraryCreated;
  }

  editFolder(folder: NodeEntry): void {
    // On successfull editing of a node, ContentManagementServices sends the updated node information which we
    // can use to trigger a right hand panel data update
    this.apiService.nodeUpdated
      .pipe(
        take(1),
        filter((node) => node.isFolder)
      )
      .subscribe((node: Node) =>
        this.treeViewService.triggerTreeViewReload({
          type: FolderOperationType.EDIT,
          folderId: node.id,
          folder: {
            entry: node,
          },
        })
      );
    return this.contentManagementService.editFolder(folder);
  }

  canUpdateNode(node: NodeEntry | Node): boolean {
    return this.contentManagementService.canUpdateNode(node);
  }

  canUploadContent(folderNode: NodeEntry | Node): boolean {
    return this.contentManagementService.canUploadContent(folderNode);
  }

  /**
   * Deletes the given array of Nodes. Incase there is a source association in any of the nodes, shows a dialog
   * box instead of deleting the nodes.
   *
   * @param items array of NodeEntry to delete
   */
  async customDeleteNodes(items: NodeEntry[]) {
    const promiseArrays = items.map((node) => {
      const assocType = this.isMedia(node.entry)
        ? REFERENCED_ASSETS
        : REFERENCED_DOCUMENTS;
      return this.fetchSourceAssocForItems(node, {
        where: `(assocType='${assocType}')`,
        include: ['aspectNames', 'properties'],
      }).then((data: NodeAssociationPaging) => {
        // Create a new Map object to store the entries
        const mapOfEntries = new Map<string, any[]>();
        _.forEach(data.list.entries, ({ entry }) => {
          const { properties } = entry;
          // Get the apollo:uri property from the properties object
          const apolloURI = properties['apollo:uri'];
          // Get the entries associated with the apollo:uri from the mapOfEntries object, or create an empty array if it doesn't exist
          const entries = mapOfEntries.get(apolloURI) || [];
          entries.push(entry);
          mapOfEntries.set(apolloURI, entries);
        });

        // This code iterates through the values of a map and pushes the first element of each value into an array called associatedEntries. If the value has more than one element, it filters out the element that does not have the workingCopyAspect and pushes that element into the associatedEntries array.
        const associatedEntries = [];
        for (const value of mapOfEntries.values()) {
          if (value.length === 1) {
            associatedEntries.push(value[0]);
            continue;
          }
          const [original] = _.filter(
            value,
            ({ aspectNames }) =>
              !_.includes(aspectNames, CommonConstants.workingCopyAspect)
          );
          associatedEntries.push(original);
        }
        return _.map(associatedEntries, (minimalNode: Node) => ({
          docId: minimalNode.id,
          parentId: minimalNode.parentId,
          name: minimalNode.name,
        }));
      });
    });
    Promise.all(promiseArrays).then((array) => {
      array = array.filter((item) => !!item.length);
      if (array.length > 0) {
        const fileTypes = this.fileTypes(items);
        const associatedFilesValues = array.reduce((a, b) => a.concat(b));
        this.dialogRef.open(NotificationDialogComponent, {
          data: {
            associatedFilesValues,
            type: 'associated',
            multipleFiles: 'multiple',
            fileTypes,
          },
        });
      } else {
        this.deleteNodes(items);
      }
    });
  }

  private isMedia(node: Node): boolean {
    return (
      node.content &&
      (node.content.mimeType.includes(MediaTypeKeys.Img) ||
        node.content.mimeType.includes(MediaTypeKeys.Audio) ||
        node.content.mimeType.includes(MediaTypeKeys.Video))
    );
  }

  /**
   * Fetches source associations for a given node
   *
   * @param item NodeEntry whose source association has to be checked
   * @param options options to pass to the api
   * @returns a Promise of NodeAssociationPaging
   * @see {@link https://api-explorer.alfresco.com/api-explorer/#/nodes/listSourceAssociations}
   */
  fetchSourceAssocForItems(
    item: NodeEntry,
    options = null
  ): Promise<NodeAssociationPaging> {
    return this.apiService.nodesApi.listSourceAssociations(
      item.entry.id,
      options
    );
  }

  /**
   * checks if the nodes being deleted are an asset or non asset or both
   *
   * @param items array of NodeEntry to match for assets and non assets
   * @returns an Object with with matching condition
   */
  private fileTypes(items: NodeEntry[]) {
    const mimeTypesNoMedia = items
      .filter((entry: NodeEntry) => !this.isMedia(entry.entry))
      .map((entry: NodeEntry) => entry.entry.content?.mimeType);

    const isMediaType = items.some((entry: NodeEntry) =>
      this.isMedia(entry.entry)
    );

    return {
      isMedia: isMediaType,
      isMediaAndDoc: isMediaType && mimeTypesNoMedia.length > 0,
    };
  }
  /**
   * @private
   * OOB code from ADW origin(modified). Deletes a given array of nodes. Also checks if a given node being deleted
   *  is a folder node to trigger a tree view reload in the left hand panel.
   * @param items NodeEntry[]
   * @todo Check the changes done as per new version of ADW Origin
   */
  private deleteNodes(items: NodeEntry[]): void {
    const batch: Observable<DeletedNodeInfo>[] = [];
    let folderDeletedSuccessfully = false;
    const parenFolderId = items[0].entry.parentId;

    items.forEach((node) => {
      batch.push(
        this.deleteNode(node).pipe(
          // Check if any folder was deleted successfully.
          tap((item: { deletedNode: DeletedNodeInfo; isFolder?: boolean }) => {
            if (!folderDeletedSuccessfully) {
              folderDeletedSuccessfully =
                item.deletedNode.status && item.isFolder;
            }
          }),
          // Convert it back to the expected Object to not modify the original behaviour
          map(({ deletedNode }) => ({ ...deletedNode }))
        )
      );
    });

    forkJoin(batch).subscribe((data: DeletedNodeInfo[]) => {
      const status = this.processStatus(data);
      const message = this.getDeleteMessage(status);

      if (message && status.someSucceeded) {
        message.duration = 10000;
        message.userAction = new SnackbarUserAction(
          'APP.ACTIONS.UNDO',
          new UndoDeleteNodesAction([...status.success])
        );
      }

      const nodeFacade = this.injector.get(NodeActionsFacade);
      nodeFacade.dispatchAction(message);

      if (status.someSucceeded) {
        if (folderDeletedSuccessfully) {
          this.treeViewService.triggerTreeViewReload({
            type: FolderOperationType.DELETE,
            parentFolderId: parenFolderId,
          });
        }
        this.nodesDeleted.next();
        const documentNodeFacade = this.injector.get(NodeActionsFacade);
        documentNodeFacade.dispatchReloadDocumentList();
      }
    });
  }

  /**
   * @private
   * OOB code from ADW Origin(modified). Deletes the given node.
   * @param node node to be deleted.
   * @returns an object with DeletedNodeInfo and isFolder properties.
   * @todo Check the changes done as per new version of ADW Origin
   */
  private deleteNode(
    node: any
  ): Observable<{ deletedNode: DeletedNodeInfo; isFolder?: boolean }> {
    const { name, isFolder } = node.entry as Node;
    const id = node.entry.nodeId || node.entry.id;

    return this.contentApiService.deleteNode(id).pipe(
      map(() => ({
        deletedNode: {
          id,
          name,
          status: 1,
        },
        isFolder,
      })),
      catchError(() =>
        of({
          deletedNode: {
            id,
            name,
            status: 0,
          },
        })
      )
    );
  }

  /**
   * @privte
   * OOB code from ADW Origin(unmodified). Checks the deletion status of the nodes deleted
   * @param data an array of DeletedNode
   * @returns a DeleteStatus Object
   * @todo Check the changes done as per new version of ADW Origin
   */
  private processStatus(data: Array<{ status: number }> = []): DeleteStatus {
    const status = {
      fail: [],
      success: [],
      get someFailed() {
        return !!this.fail.length;
      },
      get someSucceeded() {
        return !!this.success.length;
      },
      get oneFailed() {
        return this.fail.length === 1;
      },
      get oneSucceeded() {
        return this.success.length === 1;
      },
      get allSucceeded() {
        return this.someSucceeded && !this.someFailed;
      },
      get allFailed() {
        return this.someFailed && !this.someSucceeded;
      },
      reset() {
        this.fail = [];
        this.success = [];
      },
    };

    return data.reduce((acc, node) => {
      if (node.status) {
        acc.success.push(node);
      } else {
        acc.fail.push(node);
      }

      return acc;
    }, status);
  }

  /**
   * @private
   * OOB Code from ADW Origin(unmodified). Checks the deleted status of the nodes deleted and returns an appropriate message
   * @param status DeleteStatus Object
   * @returns a SnackbarAction object
   * @todo Check the changes done as per new version of ADW Origin
   */
  private getDeleteMessage(status: DeleteStatus): SnackbarAction {
    if (status.allFailed && !status.oneFailed) {
      return new SnackbarErrorAction(
        'APP.MESSAGES.ERRORS.NODE_DELETION_PLURAL',
        { number: status.fail.length }
      );
    }

    if (status.allSucceeded && !status.oneSucceeded) {
      return new SnackbarInfoAction('APP.MESSAGES.INFO.NODE_DELETION.PLURAL', {
        number: status.success.length,
      });
    }

    if (status.someFailed && status.someSucceeded && !status.oneSucceeded) {
      return new SnackbarWarningAction(
        'APP.MESSAGES.INFO.NODE_DELETION.PARTIAL_PLURAL',
        {
          success: status.success.length,
          failed: status.fail.length,
        }
      );
    }

    if (status.someFailed && status.oneSucceeded) {
      return new SnackbarWarningAction(
        'APP.MESSAGES.INFO.NODE_DELETION.PARTIAL_SINGULAR',
        {
          success: status.success.length,
          failed: status.fail.length,
        }
      );
    }

    if (status.oneFailed && !status.someSucceeded) {
      return new SnackbarErrorAction('APP.MESSAGES.ERRORS.NODE_DELETION', {
        name: status.fail[0].name,
      });
    }

    if (status.oneSucceeded && !status.someFailed) {
      return new SnackbarInfoAction(
        'APP.MESSAGES.INFO.NODE_DELETION.SINGULAR',
        { name: status.success[0].name }
      );
    }

    return null;
  }

  /**
   * OOB code from ADW Origin(modified). Creates a new folder and triggers the reload of tree view.
   *
   * @param parentNodeId parent folder node id
   * @todo Check the changes done as per new version of ADW Origin
   */
  createFolder(parentNodeId: string) {
    const dialogInstance = this.dialogRef.open(FolderDialogComponent, {
      data: {
        parentNodeId,
        createTitle: undefined,
        nodeType: 'cm:folder',
      },
      width: '400px',
    });

    dialogInstance.componentInstance.error.subscribe((message: string) => {
      const nodeFacade = this.injector.get(NodeActionsFacade);
      nodeFacade.dispatchSnackbarError(message);
    });

    dialogInstance.afterClosed().subscribe((node: Node) => {
      if (node) {
        this.treeViewService.triggerTreeViewReload({
          type: FolderOperationType.CREATE,
          parentFolderId: parentNodeId,
        });
        const nodeFacade = this.injector.get(NodeActionsFacade);
        nodeFacade.dispatchReloadDocumentList();
      }
    });
  }

  /**
   * OOB Code from ADW Origin(modified). Undo the delete operation on the array of DeleteNodeInfo.
   *
   * @param items array of DeletedNodeInfo created when the nodes were deleted.
   * @todo Check the changes done as per new version of ADW Origin
   */
  undoDeleteNodes(items: DeletedNodeInfo[]): void {
    const batch: Observable<DeletedNodeInfo>[] = [];
    let folderRestoredSuccessfully = false;
    let parenFolderId = '';

    items.forEach((item) => {
      batch.push(
        this.undoDeleteNode(item).pipe(
          tap(
            (itemEntry: {
              deletedNode: DeletedNodeInfo;
              isFolder?: boolean;
              parentFolderId?: string;
            }) => {
              if (!folderRestoredSuccessfully) {
                folderRestoredSuccessfully =
                  itemEntry.deletedNode.status && itemEntry.isFolder;
                parenFolderId = itemEntry.parentFolderId;
              }
            }
          ),
          map(({ deletedNode }) => ({ ...deletedNode }))
        )
      );
    });

    forkJoin(batch).subscribe((data) => {
      const processedData = this.processStatus(data);
      const nodeFacade = this.injector.get(NodeActionsFacade);

      if (processedData.fail.length) {
        const message = this.getUndoDeleteMessage(processedData);
        nodeFacade.dispatchAction(message);
      }

      if (processedData.someSucceeded) {
        if (folderRestoredSuccessfully) {
          this.treeViewService.triggerTreeViewReload({
            type: FolderOperationType.UNDO_DELETE,
            parentFolderId: parenFolderId,
          });
        }
        nodeFacade.dispatchReloadDocumentList();
      }
    });
  }

  /**
   * @private
   * OOB code from ADW Origin(modified). Undo the given DeleteNodeInfo object
   * @param item an Object of DeletedNodeInfo to restore
   * @returns an Observable which contains restored node's undo status, parent folder id and a boolean for folder.
   * @todo Check the changes done as per new version of ADW Origin.
   */
  private undoDeleteNode(item: DeletedNodeInfo): Observable<{
    deletedNode: DeletedNodeInfo;
    isFolder?: boolean;
    parentFolderId?: string;
  }> {
    const { id, name } = item;

    return this.contentApiService.restoreNode(id).pipe(
      map((node: NodeEntry) => ({
        deletedNode: {
          id,
          name,
          status: 1,
        },
        isFolder: node.entry.isFolder,
        parentFolderId: node.entry.parentId,
      })),
      catchError(() =>
        of({
          deletedNode: {
            id,
            name,
            status: 0,
          },
          parentFolderId: '',
        })
      )
    );
  }

  /**
   * OOB Code from ADW origin(unmodified).
   *
   * @param status a DeleteStatus object which contains status of recently restored nodes
   * @returns a SnackBarAction object with appropriate message.
   */
  private getUndoDeleteMessage(status: DeleteStatus): SnackbarAction {
    if (status.someFailed && !status.oneFailed) {
      return new SnackbarErrorAction(
        'APP.MESSAGES.ERRORS.NODE_RESTORE_PLURAL',
        { number: status.fail.length }
      );
    }

    if (status.oneFailed) {
      return new SnackbarErrorAction('APP.MESSAGES.ERRORS.NODE_RESTORE', {
        name: status.fail[0].name,
      });
    }
    return null;
  }

  /**
   * Creates a new library and updates the list of sites and current selected site in the store.
   */
  createLibrary() {
    let newSite = '';
    this.libraryCreated
      .pipe(
        tap((site: SiteEntry) => {
          this.contentService.libraryCreated = true;
          newSite = site.entry.id;
        }),
        switchMap(() => this.contentService.getSites()),
        take(1)
      )
      .subscribe((sites: WkSiteModel[]) => {
        const nodeFacade = this.injector.get(NodeActionsFacade);
        if (sites && sites.length) {
          nodeFacade.dispatchSetSites(sites);
        }
        this.contentService.libraryCreated = false;
        nodeFacade.dispatchSetCurrentSite(newSite);
      });
    return this.contentManagementService.createLibrary();
  }

  public managePermissions(node: NodeEntry): void {
    if (node && node.entry) {
      const { nodeId, id } = node.entry as any;
      const siteId = node.entry['guid'];
      const targetId = siteId || nodeId || id;

      if (targetId) {
        this.dialogRef.open(WkNodePermissionsDialogComponent, {
          data: { nodeId: targetId },
          panelClass: 'aca-permissions-dialog-panel',
          width: '730px',
        });
      } else {
        const nodeFacade = this.injector.get(NodeActionsFacade);
        nodeFacade.dispatchSnackbarError('APP.MESSAGES.ERRORS.PERMISSION');
      }
    }
  }

  showFolderInformation(node: NodeEntry) {
    this.dialogRef.open(DialogComponent, {
      data: {
        title: 'APP.FOLDER_INFO.TITLE',
        confirmButtonTitle: 'APP.FOLDER_INFO.DONE',
        isCancelButtonHidden: true,
        isCloseButtonHidden: false,
        dialogSize: DialogSize.Large,
        contentComponent: FolderInformationComponent,
        componentData: node.entry,
      },
      width: '700px',
    });
  }
}
